import React from 'react';

import { Alert } from '@components/Alert';
import { Button } from '@components/Button';
import { TextInput } from '@components/Forms/TextInput';
import { useResetPassword } from '@hooks/mutation/useResetPassword';
import { useUser } from '@hooks/queries/useUser';
import LockOutlinedIcon from '@material-ui/icons/LockOutlined';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { FormError } from '@utils/errors';
import { formStrings, createValidate, requiredStringField } from '@utils/forms';
import { createStrings } from '@utils/local';
import { PageMeta } from '@utils/pages';
import { Form, Formik } from 'formik';
import * as t from 'io-ts';

const strings = createStrings({
  ja: {
    title: 'パスワードリセット',
    passwordPlaceholder: '新しいパスワード',
    passwordPlaceholderConfirm: '新しいパスワード（再入力）',
    sureButton: '確認',
  },
});

const schema = t.type({
  new_password1: requiredStringField(formStrings.password),
  new_password2: requiredStringField(formStrings.password),
  uid: t.number,
});

const initialValues: t.TypeOf<typeof schema> = {
  new_password1: '',
  new_password2: '',
  uid: 0,
};

export const ResetPassword: React.FC = () => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };
  const { data, mutateAsync, isError, error, isSuccess, reset } = useResetPassword();
  const user = useUser();

  return (
    <div className={classes.container}>
      <div className={classes.middleContainer}>
        <Formik
          validate={createValidate(schema)}
          initialValues={initialValues}
          onSubmit={async (values) => {
            await mutateAsync({
              new_password1: values.new_password1,
              new_password2: values.new_password2,
              uid: user?.data?.id || 0,
            });
          }}
        >
          <>
            <Alert open={isError} severity="error" onClose={reset}>
              {error instanceof FormError && error.getDisplayMessage()}
            </Alert>
            <Alert open={isSuccess} severity="success" onClose={reset}>
              {data?.detail}
            </Alert>
            <Form className={classes.formContainer}>
              <div className={classes.title}>{strings.title}</div>
              <TextInput
                name="new_password1"
                type="password"
                autoComplete="current-password"
                placeholder={strings.title}
                noLabel
                iconBefore={<LockOutlinedIcon className={classes.personIcon} />}
              />
              <TextInput
                name="new_password2"
                type="password"
                autoComplete="current-password"
                placeholder={strings.passwordPlaceholderConfirm}
                noLabel
                iconBefore={<LockOutlinedIcon className={classes.personIcon} />}
              />
              <Button
                text={strings.sureButton}
                color="primary"
                className={classes.sendButton}
                type="submit"
              />
            </Form>
          </>
        </Formik>
      </div>
    </div>
  );
};

export const meta: PageMeta = {};

const useStyles = makeMuiStyles((theme, common) => ({
  container: {
    ...common.flexCenter,
    backgroundColor: theme.palette.text.primary,
    flex: 1,
    height: '100vh',
  },
  middleContainer: {
    width: 500,
    background: theme.palette.secondary.contrastText,
  },
  formContainer: {
    ...common.flexColumn,
    ...common.verticalSpace2,
    padding: '77px 69px',
    marginTop: 30,
  },
  title: {
    ...common.normalText,
    ...common.flexCenter,
    fontSize: 24,
    fontWeight: 'bold',
    color: theme.palette.text.primary,
  },
  personIcon: {
    paddingRight: 10,
    width: '1.6rem',
    height: '1.6rem',
    color: theme.palette.grey[500],
  },
  tipsContainer: {
    ...common.flexCenter,
    ...common.flexColumn,
  },
  forgetPassword: {
    color: theme.palette.secondary.contrastText,
  },
  tipsInfo: {
    color: theme.palette.grey[500],
    padding: '0 24px',
  },
  sendButton: {
    borderRadius: 27,
  },
  tipsBack: {
    ...common.normalText,
    color: theme.palette.secondary.main,
    marginTop: 20,
  },
  sendMsg: {},
}));
